import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { useAuth } from './AuthContext';
import { setPageMeta } from './pageMeta';

const fallbackLanguage = 'en';

const pageCopy = {
  '/find-clothes-by-picture': {
    asset: '/how-it-works-assets/screen-upload.svg',
    phoneLabel: {
      en: 'Picture search',
      it: 'Ricerca da foto',
      de: 'Bildsuche',
      fr: 'Recherche par image',
      ro: 'Cautare dupa poza',
      es: 'Busqueda por foto',
    },
    phoneResult: {
      en: 'Jacket, jeans, shoes, bag',
      it: 'Giacca, jeans, scarpe, borsa',
      de: 'Jacke, Jeans, Schuhe, Tasche',
      fr: 'Veste, jean, chaussures, sac',
      ro: 'Jacheta, blugi, pantofi, geanta',
      es: 'Chaqueta, jeans, zapatos, bolso',
    },
    en: {
      title: 'Find Clothes From Public Looks | AI Outfit & Similar Clothing Finder',
      description: 'Start from public celebrity and creator outfits, detect the visible pieces, try the vibe on your photos and discover similar clothing online.',
      kicker: 'AI clothing finder',
      h1: 'Find clothes from public outfits',
      intro: 'Outfit Detective starts from public celebrity and creator looks: it reads the visible pieces, colors and silhouette, helps you preview the vibe on your own photos, then suggests similar or verified products when available.',
      note: 'Public looks are used for inspiration. Product results are similar-style suggestions unless an item is clearly verified.',
      primaryCta: 'Explore public looks',
      secondaryCta: 'How it works',
      flowKicker: 'Simple flow',
      flowTitle: 'From public post to your version',
      steps: [
        ['Start from a public look', 'Use celebrity, creator or Fit Wall posts as the outfit reference.'],
        ['AI reads the visible pieces', 'Outfit Detective looks for category, color, silhouette and styling clues.'],
        ['Try and shop the vibe', 'Preview the look on your photos, then browse similar or verified alternatives by item.'],
      ],
      examplesTitle: 'What you can search with',
      examplesKicker: 'Useful examples',
      examples: [
        ['Celebrity outfit', 'Break a public look into pieces you can understand and reuse.', '/how-it-works-assets/screen-detect.svg'],
        ['Creator post', 'Find similar shapes, colors and items without needing the original brand.', '/how-it-works-assets/screen-upload.svg'],
        ['Try-on preview', 'Test the vibe later with your private photos.', '/how-it-works-assets/screen-tryon.svg'],
      ],
      trustTitle: 'Built for inspiration, not fake certainty',
      trustCopy: 'Outfit Detective helps you move from a public style idea to a personal preview and useful product options. We avoid promising exact matches unless an item is clearly known, and private photos belong in the logged-in try-on flow.',
      trust: ['Public full-outfit posts usually work better than tiny crops.', 'Clear shoes, bags and accessories improve product ideas.', 'Your personal try-on photos stay behind login.'],
      faqTitle: 'Before you try a look',
      faqs: [
        ['Can Outfit Detective find the exact item?', 'Sometimes a result may be close, but the product is built around visually similar suggestions. We label alternatives as suggestions unless an item is clearly verified.'],
        ['Is the product about uploading random photos?', 'No. The core flow starts from public looks and lets you replicate the vibe on your own private photos.'],
        ['What happens to personal photos?', 'Personal try-on photos are handled inside your account flow and are used for your preview experience.'],
        ['Does it work only for celebrity outfits?', 'No. It also works with creator looks, public Fit Wall posts and style moments that have clear visible pieces.'],
      ],
      finalKicker: 'Ready to try a look?',
      finalTitle: 'Start from a public outfit.',
      finalCopy: 'Browse public looks first, then use your private photos only when you want to preview the outfit on yourself.',
    },
    it: {
      title: 'Trova vestiti dai look pubblici | Outfit Detective',
      description: 'Parti da outfit pubblici di celebrity e creator, rileva i capi visibili, prova il vibe sulle tue foto e scopri vestiti simili online.',
      kicker: 'Ricerca vestiti AI',
      h1: 'Trova vestiti dai look pubblici',
      intro: 'Outfit Detective parte da look pubblici di celebrity e creator: legge capi visibili, colori e silhouette, ti aiuta a provare quel vibe sulle tue foto e poi suggerisce prodotti simili o verificati quando disponibili.',
      note: 'I look pubblici servono come ispirazione. I prodotti sono suggerimenti simili, salvo quando un capo e chiaramente verificato.',
      primaryCta: 'Esplora look pubblici',
      secondaryCta: 'Come funziona',
      flowKicker: 'Flusso semplice',
      flowTitle: 'Dal post pubblico alla tua versione',
      steps: [
        ['Parti da un look pubblico', 'Usa post di celebrity, creator o Fit Wall come riferimento outfit.'],
        ['AI legge i capi visibili', 'Outfit Detective cerca categoria, colore, silhouette e segnali di styling.'],
        ['Prova e trova alternative', 'Vedi il vibe sulle tue foto, poi sfoglia prodotti simili o verificati per capo.'],
      ],
      examplesTitle: 'Cosa puoi cercare',
      examplesKicker: 'Esempi utili',
      examples: [
        ['Outfit celebrity', 'Scomponi un look pubblico in capi chiari e riutilizzabili.'],
        ['Post creator', 'Trova forme, colori e capi simili senza conoscere il brand originale.'],
        ['Preview try-on', 'Prova il vibe dopo sulle tue foto private.'],
      ],
      trustTitle: 'Ispirazione, non false certezze',
      trustCopy: 'Outfit Detective ti porta da un idea pubblica a una preview personale e a prodotti utili. Evitiamo promesse di capi identici se l item non e chiaramente verificato.',
      trust: ['I post outfit pubblici completi funzionano meglio dei crop piccoli.', 'Scarpe, borse e accessori visibili migliorano i risultati.', 'Le foto del tuo try-on restano dietro login.'],
      faqTitle: 'Prima di provare un look',
      faqs: [
        ['Trova il prodotto esatto?', 'A volte un risultato puo essere molto vicino, ma il prodotto e pensato per alternative visivamente simili.'],
        ['Il prodotto e solo caricare foto a caso?', 'No. Il flusso principale parte da look pubblici e ti permette di replicare quel vibe sulle tue foto private.'],
        ['Cosa succede alle foto personali?', 'Le foto try-on personali restano nel flusso account e servono alla preview.'],
        ['Funziona solo per celebrity?', 'No. Funziona anche con creator look, post pubblici del Fit Wall e momenti stile con capi visibili.'],
      ],
      finalKicker: 'Pronto a provare un look?',
      finalTitle: 'Parti da un outfit pubblico.',
      finalCopy: 'Guarda prima i look pubblici, poi usa le tue foto private solo quando vuoi vedere come ti starebbe l outfit.',
    },
    de: {
      title: 'Kleidung per Bild finden | Outfit Detective',
      description: 'Lade ein Foto oder einen Screenshot hoch und Outfit Detective findet visuell aehnliche Kleidung, Schuhe, Taschen und Accessoires.',
      kicker: 'AI Kleidungssuche',
      h1: 'Kleidung per Bild finden',
      intro: 'Nutze ein Foto oder Outfit-Screenshot, um visuell aehnliche Kleidung, Schuhe, Taschen und Accessoires zu finden. Outfit Detective liest Pieces, Farben und Silhouette und macht daraus nuetzliche Produktideen.',
      flowKicker: 'Einfacher Ablauf',
      flowTitle: 'Vom Bild zu aehnlichen Pieces',
      steps: [
        ['Foto oder Screenshot hochladen', 'Starte mit einem gespeicherten Outfit, einem Bild aus der Galerie oder einem oeffentlichen Style-Moment.'],
        ['AI liest sichtbare Pieces', 'Outfit Detective erkennt Kategorie, Farbe, Silhouette und Styling-Signale.'],
        ['Aehnliche Alternativen ansehen', 'Nutze Produktideen als Inspiration, nicht als Garantie fuer exakte Treffer.'],
      ],
      examplesTitle: 'Womit du suchen kannst',
      examplesKicker: 'Nuetzliche Beispiele',
      examples: [
        ['Creator-Screenshot', 'Mach aus einem gespeicherten Look einen klaren Outfit-Breakdown.'],
        ['Street-Style Foto', 'Finde aehnliche Formen, Farben und Pieces online.'],
        ['Try-on Idee', 'Speichere den Vibe und teste ihn spaeter mit deinen privaten Fotos.'],
      ],
      trustTitle: 'Inspiration statt falscher Sicherheit',
      trustCopy: 'Outfit Detective bringt dich von einer visuellen Idee zu aehnlichen Produkten. Exakte Treffer werden nicht versprochen, wenn ein Item nicht klar verifiziert ist.',
      trust: ['Komplette Outfit-Fotos funktionieren besser als kleine Crops.', 'Sichtbare Schuhe, Taschen und Accessoires verbessern die Ergebnisse.', 'Dein persoenlicher Try-on bleibt hinter dem Login.'],
      faqTitle: 'Vor dem Upload',
      faqs: [
        ['Findet Outfit Detective das exakte Produkt?', 'Manchmal ist ein Ergebnis sehr nah, aber die Suche ist fuer visuell aehnliche Alternativen gebaut.'],
        ['Kann ich Screenshots nutzen?', 'Ja, wenn du das Bild in deinem persoenlichen Flow nutzen darfst.'],
        ['Was passiert mit persoenlichen Fotos?', 'Persoenliche Try-on Fotos bleiben im Account-Flow und dienen deiner Preview.'],
        ['Funktioniert es nur fuer Celebrity-Outfits?', 'Nein. Es funktioniert mit gespeicherten Outfits, Creator-Looks, Street-Style und eigenen Fotos.'],
      ],
      finalKicker: 'Bereit fuer ein Bild?',
      finalTitle: 'Starte mit einem gespeicherten Outfit.',
      finalCopy: 'Lade ein Foto hoch oder sieh dir zuerst Beispiele im Fit Wall an.',
    },
    fr: {
      title: 'Trouver des vetements par image | Outfit Detective',
      description: 'Ajoutez une photo ou un screenshot et Outfit Detective trouve des vetements, chaussures, sacs et accessoires visuellement similaires.',
      kicker: 'Recherche vetements AI',
      h1: 'Trouver des vetements par image',
      intro: 'Utilisez une photo ou un screenshot outfit pour trouver des vetements, chaussures, sacs et accessoires visuellement similaires. Outfit Detective lit les pieces, couleurs et silhouette puis cree des idees produit utiles.',
      flowKicker: 'Flow simple',
      flowTitle: 'De l image aux pieces similaires',
      steps: [
        ['Ajoutez une photo ou screenshot', 'Partez d un outfit sauvegarde, d une photo ou d un moment style public.'],
        ['L AI lit les pieces visibles', 'Outfit Detective cherche categorie, couleur, silhouette et signaux de styling.'],
        ['Explorez des alternatives similaires', 'Utilisez les idees produit comme inspiration, pas comme promesse exact-match.'],
      ],
      examplesTitle: 'Ce que vous pouvez chercher',
      examplesKicker: 'Exemples utiles',
      examples: [
        ['Screenshot creator', 'Transformez un look sauvegarde en breakdown clair.'],
        ['Photo street-style', 'Trouvez des formes, couleurs et pieces similaires en ligne.'],
        ['Idee try-on', 'Sauvegardez le vibe et testez-le plus tard avec vos photos privees.'],
      ],
      trustTitle: 'Inspiration, pas fausse certitude',
      trustCopy: 'Outfit Detective transforme une idee visuelle en produits similaires. Nous evitons de promettre des produits exacts quand l item n est pas clairement verifie.',
      trust: ['Les photos outfit completes marchent mieux que les petits crops.', 'Chaussures, sacs et accessoires visibles ameliorent les resultats.', 'Votre try-on personnel reste derriere le login.'],
      faqTitle: 'Avant l upload',
      faqs: [
        ['Trouve-t-il le produit exact?', 'Parfois un resultat peut etre tres proche, mais le produit est concu pour des alternatives visuellement similaires.'],
        ['Puis-je utiliser des screenshots?', 'Oui, si vous avez le droit d utiliser cette image dans votre flow personnel.'],
        ['Que deviennent les photos personnelles?', 'Les photos try-on personnelles restent dans le flow compte et servent a la preview.'],
        ['Est-ce seulement pour les celebrities?', 'Non. Cela fonctionne avec looks sauvegardes, creator looks, street-style et vos propres photos.'],
      ],
      finalKicker: 'Pret a essayer une image?',
      finalTitle: 'Partez d un outfit sauvegarde.',
      finalCopy: 'Ajoutez une photo quand vous voulez, ou regardez d abord le Fit Wall.',
    },
    ro: {
      title: 'Gaseste haine dupa poza | Outfit Detective',
      description: 'Incarca o poza sau un screenshot si Outfit Detective gaseste haine, pantofi, genti si accesorii vizual similare.',
      kicker: 'Cautare haine AI',
      h1: 'Gaseste haine dupa poza',
      intro: 'Foloseste o poza sau un screenshot cu outfit pentru a gasi haine, pantofi, genti si accesorii vizual similare. Outfit Detective citeste piesele, culorile si silueta, apoi le transforma in idei utile de produse.',
      flowKicker: 'Flux simplu',
      flowTitle: 'Din poza la piese similare',
      steps: [
        ['Incarca o poza sau screenshot', 'Porneste de la un outfit salvat, o poza din telefon sau un moment de stil public.'],
        ['AI citeste piesele vizibile', 'Outfit Detective cauta categorie, culoare, silueta si semnale de styling.'],
        ['Vezi alternative similare', 'Foloseste ideile de produse ca inspiratie, nu ca promisiune de exact match.'],
      ],
      examplesTitle: 'Ce poti cauta',
      examplesKicker: 'Exemple utile',
      examples: [
        ['Screenshot creator', 'Transforma un look salvat intr-un breakdown clar.'],
        ['Poza street-style', 'Gaseste forme, culori si piese similare online.'],
        ['Idee try-on', 'Salveaza vibeul si testeaza-l mai tarziu pe pozele tale private.'],
      ],
      trustTitle: 'Inspiratie, nu certitudini false',
      trustCopy: 'Outfit Detective te duce de la o idee vizuala la produse similare. Nu promitem produse exacte cand piesa nu este clar verificata.',
      trust: ['Pozele outfit complete functioneaza mai bine decat cropurile mici.', 'Pantofii, gentile si accesoriile vizibile imbunatatesc rezultatele.', 'Try-on-ul personal ramane dupa login.'],
      faqTitle: 'Inainte sa incarci',
      faqs: [
        ['Gaseste produsul exact?', 'Uneori rezultatul poate fi foarte apropiat, dar fluxul este construit pentru alternative vizual similare.'],
        ['Pot folosi screenshoturi?', 'Da, daca ai dreptul sa folosesti imaginea in fluxul tau personal.'],
        ['Ce se intampla cu pozele personale?', 'Pozele personale pentru try-on raman in fluxul contului si sunt folosite pentru preview.'],
        ['Merge doar pentru celebrity?', 'Nu. Merge cu outfituri salvate, lookuri de creator, street-style si pozele tale.'],
      ],
      finalKicker: 'Gata sa incerci o poza?',
      finalTitle: 'Porneste de la un outfit salvat.',
      finalCopy: 'Incarca o poza cand vrei sau vezi mai intai exemple in Fit Wall.',
    },
    es: {
      title: 'Encontrar ropa por foto | Outfit Detective',
      description: 'Sube una foto o screenshot y Outfit Detective encuentra ropa, zapatos, bolsos y accesorios visualmente similares.',
      kicker: 'Buscador ropa AI',
      h1: 'Encontrar ropa por foto',
      intro: 'Usa una foto o screenshot de outfit para encontrar ropa, zapatos, bolsos y accesorios visualmente similares. Outfit Detective lee prendas, colores y silueta, y los convierte en ideas de producto utiles.',
      flowKicker: 'Flujo simple',
      flowTitle: 'De la foto a prendas similares',
      steps: [
        ['Sube una foto o screenshot', 'Empieza con un outfit guardado, una foto del telefono o un momento de estilo publico.'],
        ['AI lee las prendas visibles', 'Outfit Detective busca categoria, color, silueta y señales de styling.'],
        ['Explora alternativas similares', 'Usa las ideas de producto como inspiracion, no como promesa de exact match.'],
      ],
      examplesTitle: 'Que puedes buscar',
      examplesKicker: 'Ejemplos utiles',
      examples: [
        ['Screenshot creator', 'Convierte un look guardado en un breakdown claro.'],
        ['Foto street-style', 'Encuentra formas, colores y prendas similares online.'],
        ['Idea try-on', 'Guarda el vibe y pruebalo luego con tus fotos privadas.'],
      ],
      trustTitle: 'Inspiracion, no falsa certeza',
      trustCopy: 'Outfit Detective te lleva de una idea visual a productos similares. Evitamos prometer productos exactos si la prenda no esta claramente verificada.',
      trust: ['Las fotos de outfit completas funcionan mejor que crops pequenos.', 'Zapatos, bolsos y accesorios visibles mejoran los resultados.', 'Tu try-on personal queda detras del login.'],
      faqTitle: 'Antes de subir',
      faqs: [
        ['Encuentra el producto exacto?', 'A veces un resultado puede estar muy cerca, pero el flujo esta pensado para alternativas visualmente similares.'],
        ['Puedo usar screenshots?', 'Si, si tienes derecho a usar esa imagen en tu flujo personal.'],
        ['Que pasa con las fotos personales?', 'Las fotos personales de try-on quedan en el flujo de cuenta y sirven para la preview.'],
        ['Funciona solo para celebrities?', 'No. Funciona con outfits guardados, creator looks, street-style y tus propias fotos.'],
      ],
      finalKicker: 'Listo para probar una foto?',
      finalTitle: 'Empieza con un outfit guardado.',
      finalCopy: 'Sube una foto cuando quieras, o mira primero ejemplos en el Fit Wall.',
    },
  },
  '/ai-outfit-finder': {
    asset: '/how-it-works-assets/screen-detect.svg',
    phoneLabel: { en: 'AI outfit finder', it: 'Outfit finder AI', de: 'AI Outfit Finder', fr: 'Finder outfit AI', ro: 'Finder outfit AI', es: 'Finder outfit AI' },
    phoneResult: { en: 'Pieces, colors, silhouette', it: 'Capi, colori, silhouette', de: 'Pieces, Farben, Silhouette', fr: 'Pieces, couleurs, silhouette', ro: 'Piese, culori, silueta', es: 'Prendas, colores, silueta' },
    en: {
      title: 'AI Outfit Finder | Find Similar Clothes From a Look',
      description: 'Use Outfit Detective as an AI outfit finder for public celebrity and creator looks. Detect pieces, preview the vibe on your photos and discover similar clothing.',
      kicker: 'AI outfit finder',
      h1: 'AI outfit finder for public looks',
      intro: 'Choose a public look you like and let Outfit Detective read the visible pieces, colors and styling signals. The goal is to replicate the outfit vibe on your own photos and connect each piece to useful product ideas.',
      note: 'Outfit Detective suggests similar alternatives and style signals. Exact products are only exact when clearly verified.',
      primaryCta: 'Start the beta',
      secondaryCta: 'Find from looks',
      flowKicker: 'What it reads',
      flowTitle: 'A clearer way to understand a look',
      steps: [
        ['Detect visible pieces', 'Jackets, tops, pants, shoes, bags and accessories are separated where the image is clear.'],
        ['Read style signals', 'Color, shape, fit and vibe help the search avoid random product lists.'],
        ['Turn it into action', 'Save, compare, try on or shop similar alternatives by item.'],
      ],
      examplesTitle: 'When it helps',
      examplesKicker: 'Use cases',
      examples: [
        ['Celebrity look', 'Understand the pieces behind a public outfit.'],
        ['Creator look', 'Find the same energy without needing the original brand.'],
        ['Your preview', 'See whether the vibe works on your own photos before shopping.'],
      ],
      trustTitle: 'Useful outfit search with boundaries',
      trustCopy: 'The finder is built to explain the look and suggest close alternatives. It does not pretend every item is official, sponsored or exact.',
      trust: ['Clear full-body images work best.', 'Similar results are grouped by visible item.', 'Private photos stay in the account flow.'],
      faqTitle: 'AI outfit finder questions',
      faqs: [
        ['What does an AI outfit finder do?', 'It reads visible clothing and styling clues from an image, then turns them into a breakdown and similar product ideas.'],
        ['Do I need to know the brand?', 'No. The search starts from visual signals, not brand tags.'],
        ['Can I use it for my own outfit?', 'Yes. You can use your own photos in the private flow.'],
        ['Will it always find products?', 'No. If the image is unclear or the catalog has poor coverage, results may be limited.'],
      ],
      finalKicker: 'Make a public look personal',
      finalTitle: 'Find the pieces behind the outfit.',
      finalCopy: 'Start with one public look, then decide whether to save, compare, try it on or shop similar pieces.',
    },
    it: {
      title: 'AI Outfit Finder | Trova capi simili da un look',
      description: 'Usa Outfit Detective come AI outfit finder per look pubblici di celebrity e creator. Rileva i capi, prova il vibe sulle tue foto e scopri vestiti simili.',
      kicker: 'AI outfit finder',
      h1: 'AI outfit finder per look pubblici',
      intro: 'Scegli un look pubblico che ti piace e lascia che Outfit Detective legga capi visibili, colori e segnali di styling. L obiettivo e replicare il vibe sulle tue foto e collegare ogni capo a idee prodotto utili.',
      note: 'Outfit Detective suggerisce alternative simili e segnali di stile. I prodotti sono esatti solo quando chiaramente verificati.',
      primaryCta: 'Inizia la beta',
      secondaryCta: 'Trova dai look',
      flowKicker: 'Cosa legge',
      flowTitle: 'Un modo piu chiaro per capire un look',
      steps: [['Rileva capi visibili', 'Giacca, top, pantaloni, scarpe, borse e accessori vengono separati quando l immagine e chiara.'], ['Legge segnali di stile', 'Colore, forma, fit e vibe aiutano a evitare liste casuali di prodotti.'], ['Trasforma il look in azione', 'Salva, confronta, prova o cerca alternative simili per capo.']],
      examplesTitle: 'Quando aiuta',
      examplesKicker: 'Casi d uso',
      examples: [['Look celebrity', 'Capisci i capi dietro un outfit pubblico.'], ['Creator look', 'Trova la stessa energia senza conoscere il brand originale.'], ['La tua preview', 'Vedi se quel vibe funziona sulle tue foto prima di cercare prodotti.']],
      trustTitle: 'Ricerca outfit utile, con confini chiari',
      trustCopy: 'Il finder spiega il look e suggerisce alternative vicine. Non finge che ogni capo sia ufficiale, sponsorizzato o identico.',
      trust: ['Le immagini full-body chiare funzionano meglio.', 'I risultati simili sono raggruppati per capo visibile.', 'Le foto private restano nel flusso account.'],
      faqTitle: 'Domande sull AI outfit finder',
      faqs: [['Cosa fa un AI outfit finder?', 'Legge capi e segnali di styling visibili in una immagine e li trasforma in breakdown e idee prodotto simili.'], ['Devo conoscere il brand?', 'No. La ricerca parte dai segnali visivi, non dai tag brand.'], ['Posso usarlo per i miei outfit?', 'Si. Puoi usare le tue foto nel flusso privato.'], ['Trovera sempre prodotti?', 'No. Se l immagine e poco chiara o il catalogo copre male quel capo, i risultati possono essere limitati.']],
      finalKicker: 'Rendi personale un look pubblico',
      finalTitle: 'Trova i capi dietro l outfit.',
      finalCopy: 'Parti da un look pubblico, poi decidi se salvarlo, confrontarlo, provarlo o cercare capi simili.',
    },
    de: {
      title: 'AI Outfit Finder | Aehnliche Kleidung aus einem Look finden',
      description: 'Nutze Outfit Detective als AI Outfit Finder fuer gespeicherte Looks, Screenshots und Style-Ideen.',
      kicker: 'AI Outfit Finder',
      h1: 'AI Outfit Finder fuer gespeicherte Looks',
      intro: 'Lade einen Look hoch und Outfit Detective liest sichtbare Pieces, Farben und Styling-Signale. Das Ziel ist ein nuetzlicher Outfit-Breakdown mit aehnlichen Produktideen.',
      flowKicker: 'Was erkannt wird',
      flowTitle: 'Ein klarerer Weg, einen Look zu verstehen',
      steps: [['Sichtbare Pieces erkennen', 'Jacken, Tops, Hosen, Schuhe, Taschen und Accessoires werden getrennt, wenn das Bild klar ist.'], ['Style-Signale lesen', 'Farbe, Form, Fit und Vibe helfen gegen zufaellige Produktlisten.'], ['In Aktion verwandeln', 'Speichern, vergleichen, anprobieren oder aehnliche Alternativen suchen.']],
      examplesTitle: 'Wann es hilft',
      examplesKicker: 'Use Cases',
      examples: [['Gespeichertes Outfit', 'Verstehe die Pieces, bevor du shoppst.'], ['Creator-Look', 'Finde die gleiche Energie ohne Original-Brand.'], ['Event-Idee', 'Baue einen Look rund um Mood oder Anlass.']],
      trustTitle: 'Nuetzliche Outfit-Suche mit Grenzen',
      trustCopy: 'Der Finder erklaert den Look und schlaegt nahe Alternativen vor. Nicht jedes Item ist offiziell, gesponsert oder exakt.',
      trust: ['Klare Full-body Bilder funktionieren am besten.', 'Aehnliche Ergebnisse werden nach sichtbarem Item gruppiert.', 'Private Fotos bleiben im Account-Flow.'],
      faqTitle: 'Fragen zum AI Outfit Finder',
      faqs: [['Was macht ein AI Outfit Finder?', 'Er liest sichtbare Kleidung und Styling-Signale aus einem Bild und erstellt Produktideen.'], ['Muss ich die Brand kennen?', 'Nein. Die Suche startet mit visuellen Signalen.'], ['Kann ich eigene Outfits nutzen?', 'Ja, im privaten Flow.'], ['Findet es immer Produkte?', 'Nein. Unklare Bilder oder Katalogluecken begrenzen Ergebnisse.']],
      finalKicker: 'Mach einen gespeicherten Look nuetzlich',
      finalTitle: 'Finde die Pieces hinter dem Outfit.',
      finalCopy: 'Starte mit einem Look und entscheide dann, ob du speicherst, vergleichst oder testest.',
    },
    fr: {
      title: 'AI Outfit Finder | Trouver des vetements similaires depuis un look',
      description: 'Utilisez Outfit Detective comme AI outfit finder pour looks sauvegardes, screenshots et idees style.',
      kicker: 'AI outfit finder',
      h1: 'AI outfit finder pour looks sauvegardes',
      intro: 'Ajoutez un look et Outfit Detective lit les pieces visibles, couleurs et signaux de styling. Le but: un breakdown utile avec idees produit similaires.',
      flowKicker: 'Ce que ca lit',
      flowTitle: 'Une facon plus claire de comprendre un look',
      steps: [['Detecter les pieces visibles', 'Vestes, tops, pantalons, chaussures, sacs et accessoires sont separes quand l image est claire.'], ['Lire les signaux de style', 'Couleur, forme, fit et vibe evitent les listes de produits aleatoires.'], ['Passer a l action', 'Sauvegarder, comparer, essayer ou chercher des alternatives similaires.']],
      examplesTitle: 'Quand c est utile',
      examplesKicker: 'Cas d usage',
      examples: [['Outfit sauvegarde', 'Comprendre les pieces avant de shopper.'], ['Creator look', 'Trouver la meme energie sans marque originale.'], ['Idee evenement', 'Construire un look autour d un mood ou occasion.']],
      trustTitle: 'Recherche outfit utile avec limites',
      trustCopy: 'Le finder explique le look et suggere des alternatives proches. Il ne pretend pas que chaque item est officiel, sponsorise ou exact.',
      trust: ['Les images full-body claires marchent mieux.', 'Les resultats similaires sont groupes par item visible.', 'Les photos privees restent dans le flow compte.'],
      faqTitle: 'Questions AI outfit finder',
      faqs: [['Que fait un AI outfit finder?', 'Il lit vetements et signaux de styling visibles puis cree des idees produit similaires.'], ['Dois-je connaitre la marque?', 'Non. La recherche part des signaux visuels.'], ['Puis-je utiliser mes propres outfits?', 'Oui, dans le flow prive.'], ['Trouvera-t-il toujours des produits?', 'Non. Image floue ou catalogue limite peuvent reduire les resultats.']],
      finalKicker: 'Rendre un look sauvegarde utile',
      finalTitle: 'Trouvez les pieces derriere l outfit.',
      finalCopy: 'Commencez avec un look, puis sauvegardez, comparez ou essayez.',
    },
    ro: {
      title: 'AI Outfit Finder | Gaseste haine similare dintr-un look',
      description: 'Foloseste Outfit Detective ca AI outfit finder pentru lookuri salvate, screenshoturi si idei de stil.',
      kicker: 'AI outfit finder',
      h1: 'AI outfit finder pentru lookuri salvate',
      intro: 'Incarca un look si Outfit Detective citeste piesele vizibile, culorile si semnalele de styling. Scopul este un breakdown util cu idei de produse similare.',
      flowKicker: 'Ce citeste',
      flowTitle: 'O metoda mai clara de a intelege un look',
      steps: [['Detecteaza piesele vizibile', 'Jachete, topuri, pantaloni, pantofi, genti si accesorii sunt separate cand imaginea e clara.'], ['Citeste semnale de stil', 'Culoarea, forma, fitul si vibeul evita listele aleatorii de produse.'], ['Transforma in actiune', 'Salveaza, compara, probeaza sau cauta alternative similare.']],
      examplesTitle: 'Cand ajuta',
      examplesKicker: 'Cazuri de folosire',
      examples: [['Outfit salvat', 'Intelege piesele inainte sa cauti.'], ['Look creator', 'Gaseste aceeasi energie fara brandul original.'], ['Idee eveniment', 'Construieste un look in jurul unui mood sau ocazii.']],
      trustTitle: 'Cautare outfit utila, cu limite clare',
      trustCopy: 'Finderul explica lookul si sugereaza alternative apropiate. Nu pretinde ca fiecare piesa e oficiala, sponsorizata sau exacta.',
      trust: ['Imaginile full-body clare functioneaza cel mai bine.', 'Rezultatele similare sunt grupate pe piesa vizibila.', 'Pozele private raman in fluxul contului.'],
      faqTitle: 'Intrebari AI outfit finder',
      faqs: [['Ce face un AI outfit finder?', 'Citeste haine si semnale vizibile dintr-o imagine si creeaza idei de produse similare.'], ['Trebuie sa stiu brandul?', 'Nu. Cautarea porneste de la semnale vizuale.'], ['Pot folosi outfiturile mele?', 'Da, in fluxul privat.'], ['Gaseste mereu produse?', 'Nu. Imaginile neclare sau lipsurile din catalog limiteaza rezultatele.']],
      finalKicker: 'Fa util un look salvat',
      finalTitle: 'Gaseste piesele din spatele outfitului.',
      finalCopy: 'Porneste cu un look, apoi salveaza, compara sau testeaza.',
    },
    es: {
      title: 'AI Outfit Finder | Encuentra ropa similar desde un look',
      description: 'Usa Outfit Detective como AI outfit finder para looks guardados, screenshots e ideas de estilo.',
      kicker: 'AI outfit finder',
      h1: 'AI outfit finder para looks guardados',
      intro: 'Sube un look y Outfit Detective lee prendas visibles, colores y senales de styling. El objetivo es un breakdown util con ideas de productos similares.',
      flowKicker: 'Que lee',
      flowTitle: 'Una forma mas clara de entender un look',
      steps: [['Detecta prendas visibles', 'Chaquetas, tops, pantalones, zapatos, bolsos y accesorios se separan cuando la imagen es clara.'], ['Lee senales de estilo', 'Color, forma, fit y vibe ayudan a evitar listas aleatorias.'], ['Convierte en accion', 'Guarda, compara, prueba o busca alternativas similares.']],
      examplesTitle: 'Cuando ayuda',
      examplesKicker: 'Casos de uso',
      examples: [['Outfit guardado', 'Entiende las prendas antes de comprar.'], ['Creator look', 'Encuentra la misma energia sin la marca original.'], ['Idea evento', 'Construye un look alrededor de un mood u ocasion.']],
      trustTitle: 'Busqueda outfit util con limites',
      trustCopy: 'El finder explica el look y sugiere alternativas cercanas. No pretende que cada prenda sea oficial, patrocinada o exacta.',
      trust: ['Las imagenes full-body claras funcionan mejor.', 'Los resultados similares se agrupan por prenda visible.', 'Las fotos privadas quedan en el flujo de cuenta.'],
      faqTitle: 'Preguntas AI outfit finder',
      faqs: [['Que hace un AI outfit finder?', 'Lee ropa y senales visibles de una imagen y crea ideas de productos similares.'], ['Debo conocer la marca?', 'No. La busqueda empieza por senales visuales.'], ['Puedo usar mis propios outfits?', 'Si, en el flujo privado.'], ['Siempre encuentra productos?', 'No. Imagenes poco claras o catalogo limitado pueden reducir resultados.']],
      finalKicker: 'Haz util un look guardado',
      finalTitle: 'Encuentra las piezas detras del outfit.',
      finalCopy: 'Empieza con un look, luego guarda, compara o prueba.',
    },
  },
  '/find-similar-clothes': {
    asset: '/how-it-works-assets/screen-tryon.svg',
    phoneLabel: { en: 'Similar clothes', it: 'Vestiti simili', de: 'Aehnliche Kleidung', fr: 'Vetements similaires', ro: 'Haine similare', es: 'Ropa similar' },
    phoneResult: { en: 'Same vibe, clearer options', it: 'Stesso vibe, opzioni chiare', de: 'Gleicher Vibe, klare Optionen', fr: 'Meme vibe, options claires', ro: 'Acelasi vibe, optiuni clare', es: 'Mismo vibe, opciones claras' },
    en: {
      title: 'Find Similar Clothes | Similar Outfit & Clothing Finder',
      description: 'Find similar clothes from public celebrity and creator outfits. Outfit Detective matches style, color, silhouette and category to useful alternatives.',
      kicker: 'Similar clothing finder',
      h1: 'Find similar clothes without knowing the brand',
      intro: 'When a public outfit has the vibe you want but the original item is unknown, start with what you can see: color, cut, shape and styling. Outfit Detective turns those clues into similar clothing ideas from online stores.',
      note: 'Similar means close in style and visual signals. It does not mean copied, official or guaranteed exact.',
      primaryCta: 'Find similar clothes',
      secondaryCta: 'Find by picture',
      flowKicker: 'Similarity signals',
      flowTitle: 'Search by what the item looks like',
      steps: [
        ['Start from a visual clue', 'Use a jacket, dress, jeans, shoes or bag visible in a look.'],
        ['Match category and style', 'The search weighs item type, color, silhouette and styling context.'],
        ['Compare alternatives', 'Use the results to choose budget, premium or close-vibe options.'],
      ],
      examplesTitle: 'Good searches',
      examplesKicker: 'Examples',
      examples: [
        ['Similar jacket', 'Look for a shape and color close to the original.'],
        ['Similar shoes', 'Compare style, sole, color and occasion.'],
        ['Similar outfit', 'Rebuild the mood with available alternatives.'],
      ],
      trustTitle: 'Similar, not counterfeit',
      trustCopy: 'Outfit Detective is for discovery and inspiration. It helps you find alternatives with a similar look, not clones or unofficial claims.',
      trust: ['No fake exact-match promise.', 'No endorsement implied from brands or creators.', 'Results depend on visible details and catalog coverage.'],
      faqTitle: 'Similar clothes questions',
      faqs: [
        ['Can I find a similar dress from a picture?', 'Yes, if the dress is clear enough for color, silhouette and category to be read.'],
        ['Can I compare cheap and premium options?', 'Where available, product ideas can cover different price lanes.'],
        ['Is this the same as Google Lens?', 'It is more outfit-focused: the app reads the look and groups product ideas by item.'],
        ['Can I use creator screenshots?', 'Use images you are allowed to use in your personal flow. Results remain similar-style suggestions.'],
      ],
      finalKicker: 'Close enough to be useful',
      finalTitle: 'Search the look, not the brand name.',
      finalCopy: 'Use a public look and let Outfit Detective turn the visible clues into similar clothing ideas.',
    },
    it: {
      title: 'Trova vestiti simili | Similar outfit finder',
      description: 'Trova vestiti simili partendo da outfit pubblici di celebrity e creator. Outfit Detective abbina stile, colore, silhouette e categoria ad alternative utili.',
      kicker: 'Finder vestiti simili',
      h1: 'Trova vestiti simili senza conoscere il brand',
      intro: 'Quando un outfit pubblico ha il vibe giusto ma il capo originale non e noto, parti da quello che si vede: colore, taglio, forma e styling. Outfit Detective trasforma questi indizi in idee di vestiti simili online.',
      note: 'Simile significa vicino per stile e segnali visivi. Non significa copia, ufficiale o garantito exact.',
      primaryCta: 'Trova vestiti simili',
      secondaryCta: 'Trova da foto',
      flowKicker: 'Segnali di similarita',
      flowTitle: 'Cerca per come appare il capo',
      steps: [['Parti da un indizio visivo', 'Usa giacca, vestito, jeans, scarpe o borsa visibili in un look.'], ['Abbina categoria e stile', 'La ricerca pesa tipo capo, colore, silhouette e contesto styling.'], ['Confronta alternative', 'Scegli opzioni budget, premium o close-vibe.']],
      examplesTitle: 'Ricerche buone',
      examplesKicker: 'Esempi',
      examples: [['Giacca simile', 'Cerca forma e colore vicini all originale.'], ['Scarpe simili', 'Confronta stile, suola, colore e occasione.'], ['Outfit simile', 'Ricostruisci il mood con alternative disponibili.']],
      trustTitle: 'Simile, non contraffatto',
      trustCopy: 'Outfit Detective serve a scoprire e prendere ispirazione. Aiuta a trovare alternative con look simile, non clone o claim non ufficiali.',
      trust: ['Nessuna falsa promessa exact-match.', 'Nessun endorsement implicito da brand o creator.', 'I risultati dipendono dai dettagli visibili e dal catalogo.'],
      faqTitle: 'Domande sui vestiti simili',
      faqs: [['Posso trovare un vestito simile da foto?', 'Si, se colore, silhouette e categoria sono abbastanza chiari.'], ['Posso confrontare opzioni economiche e premium?', 'Quando disponibili, le idee prodotto possono coprire fasce prezzo diverse.'], ['E come Google Lens?', 'E piu focalizzato sull outfit: legge il look e raggruppa idee prodotto per capo.'], ['Posso usare screenshot creator?', 'Usa immagini che puoi usare nel tuo flusso personale. I risultati restano suggerimenti simili.']],
      finalKicker: 'Abbastanza vicino da essere utile',
      finalTitle: 'Cerca il look, non il nome del brand.',
      finalCopy: 'Usa un look pubblico e lascia che Outfit Detective trasformi gli indizi visivi in idee di vestiti simili.',
    },
    de: {
      title: 'Aehnliche Kleidung finden | Outfit Detective',
      description: 'Finde aehnliche Kleidung aus einem Outfit-Foto oder Screenshot nach Stil, Farbe, Silhouette und Kategorie.',
      kicker: 'Aehnliche Kleidung',
      h1: 'Aehnliche Kleidung finden, ohne die Brand zu kennen',
      intro: 'Wenn das Original unbekannt ist, suche nach dem, was sichtbar ist: Farbe, Schnitt, Form und Styling. Outfit Detective macht daraus aehnliche Kleidungsideen.',
      flowKicker: 'Aehnlichkeits-Signale',
      flowTitle: 'Suche nach dem Aussehen des Items',
      steps: [['Mit visuellem Hinweis starten', 'Nutze Jacke, Kleid, Jeans, Schuhe oder Tasche in einem Look.'], ['Kategorie und Stil matchen', 'Item-Typ, Farbe, Silhouette und Styling-Kontext werden gewichtet.'], ['Alternativen vergleichen', 'Waehle Budget-, Premium- oder Close-vibe Optionen.']],
      examplesTitle: 'Gute Suchen',
      examplesKicker: 'Beispiele',
      examples: [['Aehnliche Jacke', 'Suche nach Form und Farbe nahe am Original.'], ['Aehnliche Schuhe', 'Vergleiche Stil, Sohle, Farbe und Anlass.'], ['Aehnliches Outfit', 'Baue den Mood mit verfuegbaren Alternativen nach.']],
      trustTitle: 'Aehnlich, nicht gefaelscht',
      trustCopy: 'Outfit Detective ist fuer Entdeckung und Inspiration. Es findet Alternativen mit aehnlichem Look, keine Klone oder inoffiziellen Claims.',
      trust: ['Keine falsche Exact-match Garantie.', 'Kein implizites Endorsement von Brands oder Creators.', 'Ergebnisse haengen von sichtbaren Details und Katalog ab.'],
      faqTitle: 'Fragen zu aehnlicher Kleidung',
      faqs: [['Kann ich ein aehnliches Kleid per Bild finden?', 'Ja, wenn Farbe, Silhouette und Kategorie klar genug sind.'], ['Kann ich guenstige und Premium-Optionen vergleichen?', 'Wo verfuegbar, koennen Produktideen verschiedene Preisbereiche abdecken.'], ['Ist das wie Google Lens?', 'Es ist outfit-fokussierter und gruppiert Ideen nach Item.'], ['Kann ich Creator-Screenshots nutzen?', 'Nutze Bilder, die du in deinem persoenlichen Flow verwenden darfst.']],
      finalKicker: 'Nah genug, um nuetzlich zu sein',
      finalTitle: 'Suche den Look, nicht den Brandnamen.',
      finalCopy: 'Nutze ein Bild und lass Outfit Detective sichtbare Hinweise in aehnliche Kleidungsideen verwandeln.',
    },
    fr: {
      title: 'Trouver des vetements similaires | Outfit Detective',
      description: 'Trouvez des vetements similaires depuis une photo outfit ou screenshot avec style, couleur, silhouette et categorie.',
      kicker: 'Finder vetements similaires',
      h1: 'Trouver des vetements similaires sans connaitre la marque',
      intro: 'Quand l item original est inconnu, partez de ce qui se voit: couleur, coupe, forme et styling. Outfit Detective transforme ces indices en idees vetements similaires.',
      flowKicker: 'Signaux de similarite',
      flowTitle: 'Chercher par apparence',
      steps: [['Partir d un indice visuel', 'Utilisez veste, robe, jean, chaussures ou sac visible dans un look.'], ['Matcher categorie et style', 'La recherche pese type d item, couleur, silhouette et contexte styling.'], ['Comparer des alternatives', 'Choisissez options budget, premium ou close-vibe.']],
      examplesTitle: 'Bonnes recherches',
      examplesKicker: 'Exemples',
      examples: [['Veste similaire', 'Chercher forme et couleur proches de l original.'], ['Chaussures similaires', 'Comparer style, semelle, couleur et occasion.'], ['Outfit similaire', 'Reconstruire le mood avec des alternatives disponibles.']],
      trustTitle: 'Similaire, pas contrefait',
      trustCopy: 'Outfit Detective sert a decouvrir et s inspirer. Il aide a trouver des alternatives au look similaire, pas des clones ou claims non officiels.',
      trust: ['Pas de fausse promesse exact-match.', 'Aucun endorsement implicite de marques ou creators.', 'Les resultats dependent des details visibles et du catalogue.'],
      faqTitle: 'Questions vetements similaires',
      faqs: [['Puis-je trouver une robe similaire depuis une image?', 'Oui, si couleur, silhouette et categorie sont assez claires.'], ['Puis-je comparer budget et premium?', 'Quand disponible, les idees produit couvrent plusieurs prix.'], ['Est-ce comme Google Lens?', 'C est plus centre outfit: le look est lu et les idees sont groupees par item.'], ['Puis-je utiliser des screenshots creator?', 'Utilisez les images que vous pouvez employer dans votre flow personnel.']],
      finalKicker: 'Assez proche pour etre utile',
      finalTitle: 'Cherchez le look, pas le nom de marque.',
      finalCopy: 'Utilisez une image et laissez Outfit Detective transformer les indices visibles en idees vetements similaires.',
    },
    ro: {
      title: 'Gaseste haine similare | Outfit Detective',
      description: 'Gaseste haine similare dintr-o poza outfit sau screenshot, dupa stil, culoare, silueta si categorie.',
      kicker: 'Finder haine similare',
      h1: 'Gaseste haine similare fara sa stii brandul',
      intro: 'Cand piesa originala nu e cunoscuta, porneste de la ce se vede: culoare, croiala, forma si styling. Outfit Detective transforma indiciile in idei de haine similare.',
      flowKicker: 'Semnale de similaritate',
      flowTitle: 'Cauta dupa cum arata piesa',
      steps: [['Porneste de la un indiciu vizual', 'Foloseste jacheta, rochie, blugi, pantofi sau geanta vizibila intr-un look.'], ['Potriveste categoria si stilul', 'Cautarea cantareste tipul piesei, culoarea, silueta si contextul.'], ['Compara alternative', 'Alege optiuni budget, premium sau close-vibe.']],
      examplesTitle: 'Cautari bune',
      examplesKicker: 'Exemple',
      examples: [['Jacheta similara', 'Cauta forma si culoare apropiate de original.'], ['Pantofi similari', 'Compara stil, talpa, culoare si ocazie.'], ['Outfit similar', 'Reconstruieste moodul cu alternative disponibile.']],
      trustTitle: 'Similar, nu contrafacut',
      trustCopy: 'Outfit Detective este pentru descoperire si inspiratie. Ajuta la gasirea de alternative cu look similar, nu clone sau promisiuni neoficiale.',
      trust: ['Fara promisiune falsa de exact match.', 'Fara endorsement implicit de la branduri sau creatori.', 'Rezultatele depind de detalii vizibile si catalog.'],
      faqTitle: 'Intrebari haine similare',
      faqs: [['Pot gasi o rochie similara din poza?', 'Da, daca culoarea, silueta si categoria sunt suficient de clare.'], ['Pot compara optiuni ieftine si premium?', 'Cand exista, ideile pot acoperi mai multe zone de pret.'], ['Este ca Google Lens?', 'Este mai focusat pe outfit si grupeaza ideile pe piesa.'], ['Pot folosi screenshoturi creator?', 'Foloseste imagini pe care ai dreptul sa le folosesti in fluxul personal.']],
      finalKicker: 'Destul de aproape ca sa fie util',
      finalTitle: 'Cauta lookul, nu numele brandului.',
      finalCopy: 'Foloseste o poza si lasa Outfit Detective sa transforme indiciile vizibile in idei de haine similare.',
    },
    es: {
      title: 'Encontrar ropa similar | Outfit Detective',
      description: 'Encuentra ropa similar desde una foto outfit o screenshot por estilo, color, silueta y categoria.',
      kicker: 'Finder ropa similar',
      h1: 'Encuentra ropa similar sin saber la marca',
      intro: 'Cuando la prenda original no se conoce, empieza por lo que se ve: color, corte, forma y styling. Outfit Detective convierte esas pistas en ideas de ropa similar.',
      flowKicker: 'Senales de similitud',
      flowTitle: 'Busca por como se ve la prenda',
      steps: [['Empieza con una pista visual', 'Usa chaqueta, vestido, jeans, zapatos o bolso visible en un look.'], ['Combina categoria y estilo', 'La busqueda pesa tipo de prenda, color, silueta y contexto.'], ['Compara alternativas', 'Elige opciones budget, premium o close-vibe.']],
      examplesTitle: 'Buenas busquedas',
      examplesKicker: 'Ejemplos',
      examples: [['Chaqueta similar', 'Busca forma y color cercanos al original.'], ['Zapatos similares', 'Compara estilo, suela, color y ocasion.'], ['Outfit similar', 'Reconstruye el mood con alternativas disponibles.']],
      trustTitle: 'Similar, no falso',
      trustCopy: 'Outfit Detective es para descubrir e inspirarse. Ayuda a encontrar alternativas con look similar, no clones ni claims no oficiales.',
      trust: ['Sin falsa promesa exact-match.', 'Sin endorsement implicito de marcas o creators.', 'Los resultados dependen de detalles visibles y catalogo.'],
      faqTitle: 'Preguntas ropa similar',
      faqs: [['Puedo encontrar un vestido similar desde una foto?', 'Si, si color, silueta y categoria son bastante claros.'], ['Puedo comparar opciones baratas y premium?', 'Cuando hay cobertura, las ideas pueden cubrir varios precios.'], ['Es como Google Lens?', 'Es mas enfocado en outfit y agrupa ideas por prenda.'], ['Puedo usar screenshots creator?', 'Usa imagenes que puedas usar en tu flujo personal.']],
      finalKicker: 'Lo bastante cerca para ser util',
      finalTitle: 'Busca el look, no el nombre de marca.',
      finalCopy: 'Usa una foto y deja que Outfit Detective convierta pistas visibles en ideas de ropa similar.',
    },
  },
};

const languageFallbacks = {
  de: {
    primaryCta: 'Beta starten',
    secondaryCta: 'Per Bild suchen',
    note: 'Die Ergebnisse sind visuell aehnliche Vorschlaege, keine garantierten exakten Produkte.',
  },
  fr: {
    primaryCta: 'Commencer la beta',
    secondaryCta: 'Trouver par image',
    note: 'Les resultats sont des suggestions visuellement similaires, pas des produits exacts garantis.',
  },
  ro: {
    primaryCta: 'Incepe beta',
    secondaryCta: 'Gaseste dupa poza',
    note: 'Rezultatele sunt sugestii vizual similare, nu produse exacte garantate.',
  },
  es: {
    primaryCta: 'Empezar beta',
    secondaryCta: 'Buscar por foto',
    note: 'Los resultados son sugerencias visualmente similares, no productos exactos garantizados.',
  },
};

function localizedPage(page, language) {
  const base = page[fallbackLanguage];
  const local = page[language] || {};
  const fallback = languageFallbacks[language] || {};
  return {
    ...base,
    ...fallback,
    ...local,
    phoneLabel: page.phoneLabel?.[language] || page.phoneLabel?.[fallbackLanguage],
    phoneResult: page.phoneResult?.[language] || page.phoneResult?.[fallbackLanguage],
    asset: page.asset,
  };
}

export default function SeoToolPage({ pageKey }) {
  const { requestLogin } = useAuth();
  const { language } = useI18n();
  const location = useLocation();
  const page = pageCopy[pageKey] || pageCopy['/find-clothes-by-picture'];
  const copy = localizedPage(page, language);

  useEffect(() => {
    setPageMeta({
      title: copy.title,
      description: copy.description,
      pathname: pageKey,
      image: '/brand/outfit-detective-og.png',
    });
  }, [copy.description, copy.title, pageKey]);

  const start = () => requestLogin({
    title: copy.primaryCta,
    copy: language === 'it'
      ? 'Entra velocemente cosi le foto try-on restano private e collegate solo al tuo account.'
      : 'Sign in quickly so your try-on photos stay private and tied only to your account.',
  });

  const secondaryPath = pageKey === '/find-clothes-by-picture' ? '/how-it-works' : '/find-clothes-by-picture';
  const canonicalPath = location.pathname.replace(/\/+$/, '') || pageKey;

  return (
    <main className="ct-how-page ct-seo-tool-page" data-page={canonicalPath}>
      <section className="ct-how-hero">
        <div className="ct-how-hero-copy">
          <p className="ct-kicker">{copy.kicker}</p>
          <h1>{copy.h1}</h1>
          <p>{copy.intro}</p>
          <div className="ct-how-actions">
            <button type="button" onClick={start}>
              <i className="ph ph-upload-simple"></i>
              <span>{copy.primaryCta}</span>
            </button>
            <Link to={secondaryPath}>
              <i className="ph ph-info"></i>
              <span>{copy.secondaryCta}</span>
            </Link>
          </div>
          <p className="ct-seo-tool-note">{copy.note}</p>
        </div>

        <div className="ct-how-demo ct-seo-tool-demo" aria-label={copy.h1}>
          <div className="ct-how-phone">
            <div className="ct-how-phone-bar">
              <span></span>
              <strong>Outfit Detective</strong>
              <i className="ph ph-dots-three-outline"></i>
            </div>
            <div className="ct-how-post">
              <img src={copy.asset} alt="" />
              <div className="ct-how-scan-box one"></div>
              <div className="ct-how-scan-box two"></div>
            </div>
            <div className="ct-how-result">
              <span>{copy.phoneLabel}</span>
              <strong>{copy.phoneResult}</strong>
              <div className="ct-how-progress"><span></span></div>
            </div>
          </div>
          <div className="ct-how-product-card">
            <span>{copy.kicker}</span>
            <strong>{copy.flowTitle}</strong>
            <em>{copy.note}</em>
          </div>
        </div>
      </section>

      <section className="ct-how-section">
        <div className="ct-how-section-head">
          <p className="ct-kicker">{copy.flowKicker}</p>
          <h2>{copy.flowTitle}</h2>
        </div>
        <div className="ct-how-steps">
          {copy.steps.map(([title, text], index) => (
            <article key={title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <i className={index === 0 ? 'ph ph-image-square' : index === 1 ? 'ph ph-sparkle' : 'ph ph-shopping-bag-open'}></i>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="ct-how-section">
        <div className="ct-how-section-head">
          <p className="ct-kicker">{copy.examplesKicker}</p>
          <h2>{copy.examplesTitle}</h2>
        </div>
        <div className="ct-seo-example-grid">
          {copy.examples.map(([title, text, image], index) => (
            <article key={title} className="ct-seo-example-card">
              <img src={image || ['/how-it-works-assets/screen-detect.svg', '/how-it-works-assets/screen-upload.svg', '/how-it-works-assets/screen-tryon.svg'][index]} alt="" />
              <div>
                <span>{copy.examplesKicker}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="ct-how-trust">
        <div>
          <p className="ct-kicker">Outfit Detective</p>
          <h2>{copy.trustTitle}</h2>
          <p>{copy.trustCopy}</p>
        </div>
        <ul>
          {copy.trust.map((item) => (
            <li key={item}><i className="ph ph-check-circle"></i>{item}</li>
          ))}
        </ul>
      </section>

      <section className="ct-seo-faq">
        <div className="ct-how-section-head">
          <p className="ct-kicker">FAQ</p>
          <h2>{copy.faqTitle}</h2>
        </div>
        {copy.faqs.map(([question, answer]) => (
          <details key={question}>
            <summary>{question}</summary>
            <p>{answer}</p>
          </details>
        ))}
      </section>

      <section className="ct-how-final">
        <img src="/brand/outfit-detective-mark-transparent.svg" alt="" />
        <p className="ct-kicker">{copy.finalKicker}</p>
        <h2>{copy.finalTitle}</h2>
        <p>{copy.finalCopy}</p>
        <div className="ct-how-actions center">
          <button type="button" onClick={start}>
            <i className="ph ph-upload-simple"></i>
            <span>{copy.primaryCta}</span>
          </button>
          <Link to="/fit-wall">Fit Wall</Link>
        </div>
      </section>
    </main>
  );
}
